// src/pages/Admin/Teacher/Grids/SharedRoutesModal.js
import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Modal,
  Button as SUIButton,
  Icon,
  Loader,
  Message,
  Dropdown,
  List,
  Image,
  Button,
} from "semantic-ui-react";
import {
  Modal as AntModal,
  Button as AntBtn,
  Select,
  Empty,
  Spin,
  Alert,
  message as antMessage,
} from "antd";
import {
  EyeOutlined,
  PlusOutlined,
  ReloadOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { DidacticRoutesAPI } from "../../../api";
import { getShareScopeLabel } from "../../../Utils/shareScope";
import { NEW_SALAS } from "../../../ui/featureFlags";
import sx from "./RutasDidacticasList/RutasDidacticasList.module.scss";
import "../../../Utils/shareScope.scss";

// Heredamos el estilo de las cards del RutasPanel
import "../EvaluationRutasComp/RutasEvaluationList/RutasPanel.scss";

import "./SharedRoutesModal.scss";

const SHARED_ITIN_IMG =
  "https://storage.googleapis.com/educa360-legacy-assets/legacy/repopublic-educa360-central/adminweb/virtualscenes/PubliEduca360/Itinerario/1754473963925_itinerarioB.jpg";

const api = new DidacticRoutesAPI();

export function SharedRoutesModal({
  open,
  onClose,
  teacher,
  accessToken,
  onAdopted,
}) {
  const { t } = useTranslation("itineraries");
  const hasInstitution = !!teacher?.institution?._id;

  // ---- estado principal (listado de compartidas) ----
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sharedInstitution, setSharedInstitution] = useState([]);
  const [sharedGlobal, setSharedGlobal] = useState([]);

  // Filtro del modal
  const FILTERS = useMemo(() => {
    const opts = [
      { key: "global", value: "global", text: t("shared.community") },
      { key: "all", value: "all", text: t("shared.all") },
    ];
    if (hasInstitution) {
      opts.unshift({
        key: "institution",
        value: "institution",
        text: t("shared.institution"),
      });
    }
    return opts;
  }, [hasInstitution, t]);

  const [filter, setFilter] = useState(
    hasInstitution ? "institution" : "global",
  );

  const loadVisible = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.getVisibleRoutes(accessToken, teacher._id);
      setSharedInstitution(res?.sharedInstitution || []);
      setSharedGlobal(res?.sharedGlobal || []);
    } catch (e) {
      console.error(e);
      setError(e.message || t("shared.loadError"));
    } finally {
      setLoading(false);
    }
  }, [accessToken, teacher?._id, t]);

  useEffect(() => {
    if (open) loadVisible();
  }, [open, loadVisible]);

  const displayedRoutes = useMemo(() => {
    if (filter === "institution") return sharedInstitution;
    if (filter === "global") return sharedGlobal;
    // "all": unión sin duplicados
    const map = new Map();
    [...sharedInstitution, ...sharedGlobal].forEach((r) => map.set(r._id, r));
    return Array.from(map.values());
  }, [filter, sharedInstitution, sharedGlobal]);

  // ---- adoptar ----
  const handleAdopt = async (route) => {
    try {
      const res = await api.adoptSharedRoute(
        accessToken,
        teacher._id,
        route._id,
      );
      if (res?.route) {
        onAdopted?.(res.route);
        antMessage.success(t("sharedModal.adoptSuccess"));
      } else {
        antMessage.success(t("sharedModal.adoptSuccessGeneric"));
      }
      await loadVisible();
    } catch (e) {
      console.error(e);
      antMessage.error(e.message || t("sharedModal.scenesLoadError"));
    }
  };

  // ---- MODAL DE ESCENAS (como en RutasDidacticasList, sin borrar) ----
  const [showScenesModal, setShowScenesModal] = useState(false);
  const [routeViewing, setRouteViewing] = useState(null);
  const [routeScenes, setRouteScenes] = useState([]);
  const [loadingScenes, setLoadingScenes] = useState(false);

  const [showRouteDescriptionModal, setShowRouteDescriptionModal] =
    useState(false);

  const fetchAllScenesByRoute = useCallback(
    async (routeId) => {
      // mismo patrón que usas en RutasDidacticasList: pedir paginado y acumular
      let all = [];
      let page = 1;
      let totalPages = 1;
      do {
        const result = await api.getScenesByRoute(
          accessToken,
          routeId,
          page,
          100,
        );
        const scenes = result?.scenes || [];
        all.push(...scenes);
        totalPages = result?.totalPages || 1;
        page++;
      } while (page <= totalPages);
      return all;
    },
    [accessToken],
  );

  const handleSeeClick = async (route) => {
    try {
      setRouteViewing(route);
      setLoadingScenes(true);
      const scenes = await fetchAllScenesByRoute(route._id);
      setRouteScenes(scenes);
      setShowScenesModal(true);
    } catch (e) {
      console.error(e);
      antMessage.error(e.message || t("sharedModal.scenesLoadError"));
    } finally {
      setLoadingScenes(false);
    }
  };

  if (NEW_SALAS) {
    return (
      <>
        <AntModal
          open={open}
          onCancel={onClose}
          width={920}
          destroyOnHidden
          title={t("sharedModal.header")}
          footer={[
            <AntBtn key="close" onClick={onClose}>
              {t("sharedModal.closeButton")}
            </AntBtn>,
          ]}
        >
          {/* Toolbar */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              marginBottom: 14,
            }}
          >
            <Select
              value={filter}
              onChange={(value) => setFilter(value)}
              options={FILTERS.map((f) => ({ label: f.text, value: f.value }))}
              style={{ minWidth: 220 }}
              size="large"
            />
            <div style={{ flex: 1 }} />
            <AntBtn icon={<ReloadOutlined />} onClick={loadVisible}>
              {t("sharedModal.refreshButton")}
            </AntBtn>
          </div>

          {error ? (
            <Alert
              type="error"
              showIcon
              message={error}
              style={{ marginBottom: 12 }}
            />
          ) : null}

          {loading ? (
            <div className={sx.center}>
              <Spin size="large" />
            </div>
          ) : displayedRoutes.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t("sharedModal.noItineraries")}
            />
          ) : (
            <div
              style={{
                display: "grid",
                gap: 12,
                gridTemplateColumns: "repeat(auto-fill, minmax(380px, 1fr))",
              }}
            >
              {displayedRoutes.map((route) => (
                <div key={route._id} className={sx.card}>
                  <div className={sx.info}>
                    <h3 className={sx.routeName}>{route.nameRute}</h3>
                    <div className={sx.meta}>
                      <span className={sx.scopeTag}>
                        <span className={sx.scopeDot} />
                        {getShareScopeLabel(route)}
                      </span>
                      <span className={sx.class}>
                        {route.classnumber
                          ? `${t("sharedModal.assignedTo")} ${route.classnumber}`
                          : t("sharedModal.noClass")}
                      </span>
                    </div>
                    {route.adoptionCount != null ? (
                      <span className={sx.class}>
                        {t("sharedModal.adoptions")}: {route.adoptionCount}
                      </span>
                    ) : null}
                    <div className={sx.actions} style={{ marginTop: 8 }}>
                      <AntBtn
                        icon={<EyeOutlined />}
                        onClick={() => handleSeeClick(route)}
                      >
                        {t("sharedModal.viewButton")}
                      </AntBtn>
                      <AntBtn
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => handleAdopt(route)}
                      >
                        {t("sharedModal.addButton")}
                      </AntBtn>
                    </div>
                  </div>
                  <div className={sx.avatar}>
                    <img src={SHARED_ITIN_IMG} alt="" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </AntModal>

        {/* Modal: escenas del itinerario compartido */}
        <AntModal
          open={showScenesModal}
          onCancel={() => setShowScenesModal(false)}
          width={620}
          destroyOnHidden
          title={`${t("sharedModal.scenesModalTitle")}: ${routeViewing?.nameRute || ""}`}
          footer={[
            <AntBtn
              key="desc"
              icon={<InfoCircleOutlined />}
              onClick={() => setShowRouteDescriptionModal(true)}
            >
              {t("sharedModal.routeDescTitle")}
            </AntBtn>,
            <AntBtn key="close" onClick={() => setShowScenesModal(false)}>
              {t("sharedModal.closeButton")}
            </AntBtn>,
          ]}
        >
          {loadingScenes ? (
            <div className={sx.center}>
              <Spin />
            </div>
          ) : !routeScenes || routeScenes.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t("sharedModal.noScenes")}
            />
          ) : (
            <div>
              {routeScenes.map((sc) => (
                <div key={sc._id} className={sx.sceneRow}>
                  <img
                    className={sx.sceneThumb}
                    src={sc?.bannertopUrl}
                    alt=""
                  />
                  <div className={sx.sceneBody}>
                    <p className={sx.sceneTitle}>{sc?.title}</p>
                    {Array.isArray(sc?.category) && sc.category.length > 0 ? (
                      <p className={sx.sceneMeta}>
                        {t("list.categoryLabel")}: {sc.category.join(", ")}
                      </p>
                    ) : null}
                    {Array.isArray(sc?.subcategory) &&
                    sc.subcategory.length > 0 ? (
                      <p className={sx.sceneMeta}>
                        {t("list.subcategoryLabel")}:{" "}
                        {sc.subcategory.join(", ")}
                      </p>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          )}
        </AntModal>

        {/* Modal: descripcion del itinerario compartido */}
        <AntModal
          open={showRouteDescriptionModal}
          onCancel={() => setShowRouteDescriptionModal(false)}
          width={520}
          destroyOnHidden
          title={t("sharedModal.routeDescTitle")}
          footer={[
            <AntBtn
              key="close"
              onClick={() => setShowRouteDescriptionModal(false)}
            >
              {t("sharedModal.closeButton")}
            </AntBtn>,
          ]}
        >
          <p>{routeViewing?.description || t("list.noDescription")}</p>
        </AntModal>
      </>
    );
  }

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        size="fullscreen"
        className="shared-routes-modal"
        closeIcon
      >
        <Modal.Header>{t("sharedModal.header")}</Modal.Header>

        <Modal.Content scrolling>
          {/* Toolbar */}
          <div className="shared-routes-toolbar">
            <Dropdown
              selection
              options={FILTERS}
              value={filter}
              onChange={(_, { value }) => setFilter(value)}
              className="shared-routes-filter"
            />
            <div className="flex-spacer" />
            <SUIButton onClick={loadVisible} icon labelPosition="left" basic>
              <Icon name="refresh" />
              {t("sharedModal.refreshButton")}
            </SUIButton>
          </div>

          {error && <Message negative content={error} />}
          {loading && <Loader active inline="centered" />}

          {!loading && displayedRoutes.length === 0 && (
            <Message info>
              <Message.Header>{t("sharedModal.noItineraries")}</Message.Header>
              <p>{t("sharedModal.noItinerariesContent")}</p>
            </Message>
          )}

          {/* Wrapper para heredar estilos de las cards */}
          <div className="rutas-panel">
            <div className="shared-routes-grid">
              {displayedRoutes.map((route) => (
                <div key={route._id} className="e-card-rute-enter">
                  {/* info */}
                  <div className="rute-info">
                    <h3 className="rute-title">{route.nameRute}</h3>

                    <p className="rute-assigned">
                      {route.classnumber ? (
                        <>
                          {t("sharedModal.assignedTo")} {route.classnumber}
                        </>
                      ) : (
                        <>{t("sharedModal.noClass")}</>
                      )}
                      {route.adoptionCount != null && (
                        <>
                          {" "}
                          · {t("sharedModal.adoptions")}:{" "}
                          <strong>{route.adoptionCount}</strong>
                        </>
                      )}
                    </p>

                    <div className="rute-buttons">
                      {/* Ver → abre modal de escenas (como en RutasDidacticasList) */}
                      <button
                        className="btn-add-route-enter btn-secondary"
                        onClick={() => handleSeeClick(route)}
                      >
                        <Icon name="eye" />
                        {t("sharedModal.viewButton")}
                      </button>

                      {/* Agregar → adopta */}
                      <button
                        className="btn-add-route-enter"
                        onClick={() => handleAdopt(route)}
                      >
                        <Icon name="add" />
                        {t("sharedModal.addButton")}
                      </button>
                    </div>
                  </div>

                  {/* imagen */}
                  <div className="rute-image">
                    <img
                      src="https://storage.googleapis.com/educa360-legacy-assets/legacy/repopublic-educa360-central/adminweb/virtualscenes/PubliEduca360/Itinerario/1754473963925_itinerarioB.jpg"
                      alt="Ruta"
                    />
                  </div>
                  <br />
                  <span className="badge-share" style={{ marginLeft: 8 }}>
                    {getShareScopeLabel(route)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </Modal.Content>

        <Modal.Actions>
          <SUIButton onClick={onClose}>
            {t("sharedModal.closeButton")}
          </SUIButton>
        </Modal.Actions>
      </Modal>

      {/* ===== Modal de escenas (idéntico al que usas en RutasDidacticasList, sin borrar) ===== */}
      <Modal
        open={showScenesModal}
        onClose={() => setShowScenesModal(false)}
        size="tiny"
      >
        <Modal.Header
          style={{ display: "flex", alignItems: "center", gap: "1rem" }}
        >
          {/* Botón “Ver descripción” (ojo azul) */}
          <Button
            icon="info"
            color="blue"
            onClick={() => setShowRouteDescriptionModal(true)}
          />
          {t("sharedModal.scenesModalTitle")}: {routeViewing?.nameRute}
        </Modal.Header>

        <Modal.Content>
          {loadingScenes ? (
            <Loader active inline="centered" />
          ) : !routeScenes || routeScenes.length === 0 ? (
            <Message info>{t("sharedModal.noScenes")}</Message>
          ) : (
            <List divided relaxed>
              {routeScenes.map((sc) => (
                <List.Item key={sc._id}>
                  <Image
                    src={sc?.bannertopUrl}
                    style={{
                      width: "50px",
                      height: "50px",
                      borderRadius: "50%",
                      objectFit: "cover",
                      marginRight: "1rem",
                    }}
                  />
                  <List.Content>
                    <List.Header>{sc?.title}</List.Header>
                    <List.Description>
                      {Array.isArray(sc?.category) &&
                        sc.category.length > 0 && (
                          <>
                            {t("list.categoryLabel")}: {sc.category.join(", ")}
                            <br />
                          </>
                        )}
                      {Array.isArray(sc?.subcategory) &&
                        sc.subcategory.length > 0 && (
                          <>
                            {t("list.subcategoryLabel")}:{" "}
                            {sc.subcategory.join(", ")}
                          </>
                        )}
                    </List.Description>
                  </List.Content>

                  {/* ⚠️ Sin botón de borrar en este modal */}
                </List.Item>
              ))}
            </List>
          )}
        </Modal.Content>

        <Modal.Actions>
          <Button onClick={() => setShowScenesModal(false)} color="grey">
            {t("sharedModal.closeButton")}
          </Button>
        </Modal.Actions>
      </Modal>

      {/* Modal de descripción de la ruta (mismo patrón que tu lista) */}
      <Modal
        open={showRouteDescriptionModal}
        onClose={() => setShowRouteDescriptionModal(false)}
        size="tiny"
      >
        <Modal.Header>{t("sharedModal.routeDescTitle")}</Modal.Header>
        <Modal.Content>
          <p>{routeViewing?.description || t("list.noDescription")}</p>
        </Modal.Content>
        <Modal.Actions>
          <Button
            onClick={() => setShowRouteDescriptionModal(false)}
            color="grey"
          >
            {t("sharedModal.closeButton")}
          </Button>
        </Modal.Actions>
      </Modal>
    </>
  );
}
